import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { isWeb } from '../services/platformService'
import useMediaQuery from '../hooks/useMediaQuery'
import { ensureAdsenseLoaded } from '../services/adsenseLoader'
import { trackAdsenseSlot } from '../services/analytics'

const CLIENT = import.meta.env.VITE_ADSENSE_CLIENT
const SLOTS = {
  siderail: import.meta.env.VITE_ADSENSE_SLOT_SIDERAIL,
  infeed: import.meta.env.VITE_ADSENSE_SLOT_INFEED,
}
const INFEED_LAYOUT_KEY = import.meta.env.VITE_ADSENSE_LAYOUT_INFEED

// Each variant mounts only at its breakpoint. AdSense throws on a zero-width
// <ins>, so a slot must be conditionally rendered rather than CSS-hidden.
const VARIANT_QUERY = {
  siderail: '(min-width: 1700px)', // clears the 1280px content + both 160px rails
  infeed: '(max-width: 1023px)',   // mobile / tablet feed only
}
const RESERVED_HEIGHT = { siderail: 600, infeed: 280 }

/**
 * AdSense display unit for FREE web users only. Pro users and the native iOS
 * webview render nothing (iOS monetises via rewarded ads, see adService).
 *
 * Variants:
 *   - 'siderail': desktop vertical unit, wide viewports only.
 *   - 'infeed':   mobile native-style in-feed card, mobile widths only.
 *
 * Reserves height while pending to avoid layout shift, and collapses (renders
 * nothing) when AdSense reports the slot unfilled.
 */
function AdSlot({ variant = 'infeed', className = '' }) {
  const { isPro } = useAuth()
  const active = useMediaQuery(VARIANT_QUERY[variant] || '(min-width: 0px)')
  const insRef = useRef(null)
  const pushedRef = useRef(false)
  const [status, setStatus] = useState('pending') // 'pending' | 'filled' | 'unfilled'

  const slot = SLOTS[variant]
  const shouldRender = isWeb() && !isPro && !!CLIENT && !!slot && active

  useEffect(() => {
    if (!shouldRender || pushedRef.current) return undefined
    pushedRef.current = true
    ensureAdsenseLoaded()
    try {
      ;(window.adsbygoogle = window.adsbygoogle || []).push({})
      trackAdsenseSlot('requested', variant)
    } catch (err) {
      console.warn('[AdSlot] push failed:', err?.message || err)
    }

    const el = insRef.current
    if (!el) return undefined
    const sync = () => {
      const s = el.getAttribute('data-ad-status')
      if (s === 'filled') {
        setStatus('filled')
        trackAdsenseSlot('filled', variant)
      } else if (s === 'unfilled') {
        setStatus('unfilled')
        trackAdsenseSlot('unfilled', variant)
      }
    }
    sync()
    const observer = new MutationObserver(sync)
    observer.observe(el, { attributes: true, attributeFilter: ['data-ad-status'] })
    return () => observer.disconnect()
  }, [shouldRender, variant])

  if (!shouldRender || status === 'unfilled') return null

  const insProps = {
    ref: insRef,
    className: 'adsbygoogle',
    style: { display: 'block', width: '100%' },
    'data-ad-client': CLIENT,
    'data-ad-slot': slot,
  }
  // True native in-feed needs the layout key generated alongside the unit in
  // the AdSense console. Until that's configured we fall back to a responsive
  // unit so the slot still renders inside its card.
  if (variant === 'infeed' && INFEED_LAYOUT_KEY) {
    insProps['data-ad-format'] = 'fluid'
    insProps['data-ad-layout-key'] = INFEED_LAYOUT_KEY
  } else {
    insProps['data-ad-format'] = 'auto'
    insProps['data-full-width-responsive'] = 'true'
  }

  return (
    <div
      className={className}
      style={{ minHeight: status === 'filled' ? undefined : RESERVED_HEIGHT[variant] }}
    >
      <ins {...insProps} />
    </div>
  )
}

export default AdSlot
